import { Attribute, Directive, forwardRef, Input, OnDestroy } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Checks if model is equal another model
 *
 * ## usage example
 *
 * <input class="input" type="password" name="confirmation" ng-model="data.confirmation" matchCheck="password">
 *
 *
 */

@Directive({
    selector: '[matchCheck]',
    standalone: true,
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MatchCheckDirective),
            multi: true
        }
    ]
})
export class MatchCheckDirective implements Validator, OnDestroy {
    private valueChangesSub?: Subscription;

    @Input({
        required: true
    })
    public matchCheck!: string;

    @Input({
        transform: (value: string) => (typeof value === 'string' ? value === 'true' : value)
    })
    public reverse: boolean = false;

    protected get isReverse() {
        if (!this.reverse) {
            return false;
        }
        return this.reverse;
    }

    ngOnDestroy() {
        this.valueChangesSub?.unsubscribe();
    }

    validate(selfControl: AbstractControl): { [key: string]: any } | null {
        // self value
        const selfControlValue = selfControl.value;

        // control value
        const otherControl = selfControl.root.get(this.matchCheck);

        const _isEqual = otherControl ? this.isEqual(selfControlValue, otherControl.value) : false;

        this.valueChangesSub?.unsubscribe();
        this.valueChangesSub = undefined;

        const apply = (isEqual: boolean) => {
            // value equal and reverse
            if (isEqual) {
                if (selfControl?.errors) {
                    delete selfControl.errors['validateEqual'];

                    if (!Object.keys(selfControl.errors).length) {
                        selfControl.setErrors(null, { emitEvent: true });
                    }
                }
            }

            // value not equal and reverse
            if (!isEqual && this.isReverse && selfControl) {
                selfControl.setErrors({ validateEqual: true });
            }
        };

        // do not validate till the field has any value
        if (!otherControl) {
            apply(true);

            return null;
        }

        if (otherControl) {
            // this code works when user types in other control putting the error in self control
            this.valueChangesSub = otherControl.valueChanges.subscribe(otherControlValue => {
                selfControl.updateValueAndValidity({
                    onlySelf: true,
                    emitEvent: false
                });
                // apply(this.isEqual(selfControlValue, otherControlValue) && (selfControl.touched || selfControl.dirty));
            });
        }

        if (!_isEqual && !this.isReverse) {
            // value not equal
            return {
                validateEqual: true
            };
        }

        apply(_isEqual);

        return null;
    }

    private isEqual(val1: any, val2: any) {
        val1 = this.unifyEmptyStrings(val1);
        val2 = this.unifyEmptyStrings(val2);

        return val1 === val2;
    }

    private unifyEmptyStrings(val: any) {
        const isDefined = this.isDefined;
        if (val === null || !isDefined(val)) {
            val = '';
        }
        return val;
    }

    private isDefined(value: any) {
        const defaultUndefined = void 0;
        return value !== defaultUndefined;
    }
}
